import { GalateaParagraphItem, getPovFontSize } from '@inkitt/types-galatea';
import React, { CSSProperties, useMemo } from 'react';

// --- Types ---

interface ParagraphEffects {
  text?: {
    styles?: CSSProperties;
  };
}

// --- Utils ---

const getGradientBackground = (colors: string[]) => {
  const start = colors[0];
  const end = colors[1] || start;
  return `linear-gradient(180deg, ${start} 0%, ${end} 100%)`;
};

// --- Subcomponents ---

const ImmersiveHighlightWeb = () => (
  <div
    style={{
      position: 'absolute',
      height: '100%',
      width: 6,
      left: -16,
      top: 0,
      borderRadius: 3,
      background: 'linear-gradient(180deg, #9E50FF 0%, #2087F9 100%)',
    }}
  />
);

const StyledProseWeb = ({ text, style, isDarkMode }: { text: string; style: CSSProperties; isDarkMode: boolean }) => {
  // Simple markdown-like parser for Galatea format
  const content = useMemo(() => {
    const parts = text.split(/(\^~.+?~\^|\^.+?\^|~.+?~)/g);

    return parts.map((part, index) => {
      if (!part) {
        return null;
      }

      if (part.startsWith('^~') && part.endsWith('~^')) {
        return (
          <span key={index} style={{ display: 'block', textAlign: 'center', fontStyle: 'italic', width: '100%' }}>
            {part.slice(2, -2)}
          </span>
        );
      }
      if (part.startsWith('^') && part.endsWith('^')) {
        return (
          <span key={index} style={{ display: 'block', textAlign: 'center', width: '100%' }}>
            {part.slice(1, -1)}
          </span>
        );
      }
      if (part.startsWith('~') && part.endsWith('~')) {
        return (
          <em key={index} style={{ fontStyle: 'italic' }}>
            {part.slice(1, -1)}
          </em>
        );
      }
      return <span key={index}>{part}</span>;
    });
  }, [text]);

  const baseStyle: CSSProperties = {
    fontFamily: 'var(--font-noto-serif), serif',
    fontSize: 19,
    color: isDarkMode ? '#fff' : '#000',
    lineHeight: 1.4,
    ...style,
  };

  return <div style={baseStyle}>{content}</div>;
};

const ChatTextMessageWeb = ({
  text,
  user,
  isCurrentUser,
  styles,
}: {
  text: string;
  user?: string;
  isCurrentUser: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles?: any;
}) => {
  const gradientStyles = useMemo(() => {
    if (styles?.backgroundColor) {
      return getGradientBackground([styles.backgroundColor, styles.backgroundColor]);
    }
    return isCurrentUser
      ? 'linear-gradient(180deg, #4F81F6 0%, #4D7CF6 100%)'
      : 'linear-gradient(180deg, #E5E5EA 0%, #E5E5EA 100%)';
  }, [styles, isCurrentUser]);

  const textColor = styles?.color || (isCurrentUser ? 'white' : 'black');

  return (
    <div
      style={{
        background: gradientStyles,
        borderRadius: 8,
        padding: 10,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {user && <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 5, color: textColor }}>{user}</div>}
      <div style={{ fontSize: 16, color: textColor, lineHeight: 1.4 }}>{text}</div>
    </div>
  );
};

const ChatImageMessageWeb = ({ url }: { url: string }) => (
  <img
    src={url}
    alt="Chat attachment"
    style={{
      maxWidth: '100%',
      borderRadius: 10,
      display: 'block',
    }}
  />
);

const ChatParagraphWeb = ({
  isCurrentUser,
  message,
  highlight,
  fontSize,
}: {
  isCurrentUser: boolean;
  message: GalateaParagraphItem;
  highlight?: boolean;
  fontSize?: number;
  scrolled?: boolean;
}) => {
  return (
    <div
      style={{
        width: '100%',
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: '66%' }}>
        {message.remote_image ? (
          <ChatImageMessageWeb url={message.remote_image} />
        ) : (
          <ChatTextMessageWeb
            text={message.text || ''}
            user={message.user}
            isCurrentUser={isCurrentUser}
            styles={message.styles}
          />
        )}
      </div>
      {highlight && <ImmersiveHighlightWeb />}
    </div>
  );
};

const SeparatorWeb = ({ text, isDarkMode }: { text: string; isDarkMode: boolean }) => (
  <div
    style={{
      marginTop: -5,
      marginBottom: 15,
      textAlign: 'center',
    }}
  >
    <span
      style={{
        fontFamily: 'var(--font-noto-serif), serif',
        fontSize: 17,
        color: isDarkMode ? '#fff' : '#000',
      }}
    >
      {text}
    </span>
  </div>
);

const PointOfViewWeb = ({ text, style, isDarkMode }: { text: string; style: CSSProperties; isDarkMode: boolean }) => (
  <p
    style={{
      fontFamily: 'var(--font-noto-serif), serif',
      ...style,
      fontWeight: 'bold',
      marginBottom: 24,
      textAlign: 'center',
      color: isDarkMode ? '#fff' : '#000',
      margin: '0 0 24px 0',
    }}
  >
    {text}
  </p>
);

type TextParagraphProps = {
  data: GalateaParagraphItem;
  fontSize?: number;
  lineHeightRatio?: number;
  highlight?: boolean;
  useJpFont?: boolean;
  isRtl: boolean;
  isDarkMode: boolean;
  scrolled?: boolean;
  effects?: ParagraphEffects;
};

const TextParagraphWeb: React.FC<TextParagraphProps> = (props) => {
  const { data, fontSize, effects, lineHeightRatio, highlight, isRtl = false } = props;
  const { text, id, separator, pov, povStyle: povBaseStyle } = data;

  const styles = useMemo(() => {
    const prose: CSSProperties = isRtl ? { textAlign: 'right' } : {};
    const computedPovStyle: CSSProperties = { ...(povBaseStyle as CSSProperties) };

    if (effects?.text?.styles) {
      Object.assign(prose, effects.text.styles);
    }

    if (fontSize && fontSize > 0) {
      prose.fontSize = fontSize;
      if (lineHeightRatio) {
        // Use unitless ratio for Web which multiplies with fontSize automatically
        prose.lineHeight = lineHeightRatio;
      }

      computedPovStyle.fontSize = getPovFontSize(computedPovStyle.fontSize, fontSize);
    }
    return { prose, pov: computedPovStyle };
  }, [isRtl, effects, fontSize, lineHeightRatio, povBaseStyle]);

  if (separator) {
    return <SeparatorWeb isDarkMode={props.isDarkMode} key={String(id)} text={text || ''} />;
  }

  return (
    <div key={String(id)} style={{ position: 'relative' }}>
      {pov && <PointOfViewWeb isDarkMode={props.isDarkMode} text={pov} style={styles.pov} />}
      {text && (
        <div style={{ position: 'relative' }}>
          <StyledProseWeb isDarkMode={props.isDarkMode} text={text} style={styles.prose} />
          {highlight && <ImmersiveHighlightWeb />}
        </div>
      )}
    </div>
  );
};

const DEFAULT_LINE_HEIGHT_RATIO = 1.4;

export interface GalateaParagraphProps {
  item: GalateaParagraphItem;
  index: number;
  fontSize: number;
  effects?: ParagraphEffects;
  lineHeightRatio?: number;
  highlight?: boolean;
  isRtl?: boolean;
  isComicPage?: boolean;
  scrolled?: boolean;
  isDarkMode?: boolean;
  chatDeviceHolder?: string;
}

export const GalateaParagraphWeb: React.FC<GalateaParagraphProps> = ({
  item,
  fontSize,
  effects,
  lineHeightRatio = DEFAULT_LINE_HEIGHT_RATIO,
  highlight,
  isRtl = false,
  scrolled = false,
  isDarkMode = false,
  chatDeviceHolder,
}) => {
  if (item.user) {
    return (
      <ChatParagraphWeb
        isCurrentUser={item.user === chatDeviceHolder}
        fontSize={fontSize}
        message={item}
        highlight={highlight}
        scrolled={scrolled}
      />
    );
  }

  return (
    <div style={{ paddingTop: 10, paddingBottom: 10 }}>
      <TextParagraphWeb
        data={item}
        effects={effects}
        fontSize={fontSize}
        lineHeightRatio={lineHeightRatio}
        highlight={highlight}
        isRtl={isRtl}
        scrolled={scrolled}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};
